import { Link } from '@mui/material';
import { ERROR_NOT_FOUND_ROUTE, LOGIN_ROUTE, SUPERADMIN_PATH } from '../routes';
import Swal from 'sweetalert2';
import {
    ClientParticipant,
    ClientUnassignedId,
    Pronoun,
    PronounLabels,
    TherapySessionDetail,
    User,
    UserRole,
} from '../types';
import { appServer } from './api';

import {
    inviteLinkRequest,
    organizationMemberInviteLinkRequest,
} from './requests';
import escapeHtml from 'escape-html';
import { GQLContractWithoutClientConsentsAndClientOverviewFragment } from '@upheal/graphql/definitions/contracts/ContractWithoutClientConsentsAndClientOverviewFragment.generated';
import { GQLClientContact } from '@upheal/graphql/definitions/schema.generated';
import { isDemoContract } from './dataUtils';

// get the demo client index from the email address - it's the number after the last underscore before @
// e.g. no-reply+demo__0fa862ff-1006-421e-a157-16570a20489a_0@upheal.io
export const demoEmailToClientIndex = (email: string) => {
    const emailParts = email.split('@');
    if (emailParts.length !== 2) return null;
    return emailParts[0].split('_').pop();
};

export const findDemoClientWithIndex = (
    contracts: GQLContractWithoutClientConsentsAndClientOverviewFragment[],
    index: number,
) => {
    return contracts.find((c) => {
        const clientEmail = c.clients.at(0)?.email;

        return (
            isDemoContract(c) &&
            clientEmail &&
            demoEmailToClientIndex(clientEmail) === String(index)
        );
    });
};

export function userDefaultRoute(user: User) {
    switch (user.role) {
        case UserRole.Admin:
            return SUPERADMIN_PATH;
        case UserRole.OrgAdmin:
        case UserRole.HealthcareProvider:
            return '/';
        case UserRole.Client:
            return LOGIN_ROUTE;
        default:
            return ERROR_NOT_FOUND_ROUTE;
    }
}

export const userConsentLink = (url: string, text: string, color: string) => {
    return (
        <Link href={url} color={color} target="_blank" rel="noreferrer">
            {text}
        </Link>
    );
};

export const handleGenerateInviteLink = (
    userId: string,
    organizationId?: string,
) => {
    appServer
        .request(
            organizationId
                ? organizationMemberInviteLinkRequest(userId, organizationId)
                : inviteLinkRequest(userId),
        )
        .then((response) => {
            const inviteId = response.data?.id;
            const linkUrl = response.data?.url;
            Swal.fire({
                titleText: `Invite link`,
                html: `Invite ID: ${escapeHtml(
                    inviteId,
                )}<br/><br/>Invite link:<br/><u>${escapeHtml(linkUrl)}</u>`,
                showCloseButton: true,
                showDenyButton: false,
                focusConfirm: true,
                confirmButtonText: 'Copy',
                confirmButtonAriaLabel: 'Copy link',
            }).then((result) => {
                if (result.isConfirmed) {
                    navigator.clipboard.writeText(linkUrl);
                    Swal.fire({
                        titleText: 'Copied!',
                        text: 'The link was copied to your clipboard.',
                        icon: 'success',
                        timer: 3000,
                        timerProgressBar: true,
                    });
                }
            });
        })
        .catch(() => {
            Swal.fire({
                titleText: 'Failed!',
                text: 'Failed to get the link at this time. Please try again later.',
                icon: 'error',
            });
        });
};

export const hasTherapistPermissions = ({
    user,
    userRole,
}: {
    user?: User | null;
    userRole?: UserRole;
}) => {
    const role = user?.role ?? userRole;
    return role === UserRole.HealthcareProvider || role === UserRole.OrgAdmin;
};

export const hasOrgAdminPermissions = ({
    user,
    userRole,
}: {
    user?: User | null;
    userRole?: UserRole;
}) => {
    const role = user?.role ?? userRole;
    return role === UserRole.OrgAdmin;
};

export const hasBillingPermissions = ({
    user,
    userRole,
}: {
    user?: User | null;
    userRole?: UserRole;
}) => {
    const role = user?.role ?? userRole;
    return role === UserRole.OrgAdmin;
};

export const getClientContactForSoloContract = (
    contract: GQLContractWithoutClientConsentsAndClientOverviewFragment,
) => contract.clients.at(0);

const TEMP_USER_ID_PATTERN = /^00000000-0000-0000-0000-([0-9a-fA-F]{1,12})$/;

export const isAnonymousTempId = (id: string) => TEMP_USER_ID_PATTERN.test(id);

export const getUserTempIdReadableFormat = (tempId: string) => {
    const match = tempId.match(TEMP_USER_ID_PATTERN);

    if (!match) {
        return null;
    }

    return parseInt(match[1], 16);
};

/**
 * Will simply return clients from the contract if it is provided.
 * If there's no session detail or transcript, will default to single anonymous
 * client participant.
 *
 * If contract is not passed in (anonymous sessions), it will parse out client
 * speakers from transcript, which are provided hardcoded pseudo-UUID in this form
 *
 * 00000000-0000-0000-0000-0000000000XX
 *
 * where XX is hexadecimal assigned to this anonymous speaker by our BE.
 *
 * This is turned into readable integer format e.g. Client 1, Client 15.
 */
export const getClientParticipants = (
    sessionDetail?: TherapySessionDetail | null,
    contract?: GQLContractWithoutClientConsentsAndClientOverviewFragment,
): Array<ClientParticipant> => {
    if (sessionDetail?.clientPresence === false) {
        return [];
    }

    if (contract) {
        return contract.clients;
    }

    if (!sessionDetail || !sessionDetail.transcript) {
        return [{ id: ClientUnassignedId, firstName: 'Client', lastName: '' }];
    }

    const anonymousSpeakerIds = Object.values(
        sessionDetail.transcript.speakerUserIdMapping,
    ).filter(isAnonymousTempId);

    return anonymousSpeakerIds.map((speakerId, index) => {
        const readableId = getUserTempIdReadableFormat(speakerId);

        return {
            id: speakerId,
            firstName: `Client ${readableId ?? index}`,
            lastName: '',
        };
    });
};

export const getPreferredPronoun = (client?: GQLClientContact) => {
    return client?.pronoun
        ? PronounLabels[client.pronoun]
        : PronounLabels[Pronoun.NeutralThem]; // Fallback default per our AI pipeline
};

const isPronoun = (value: string): value is Pronoun =>
    Object.values(Pronoun).includes(value);

export const getPronounOptionLabel = (value: string): string =>
    isPronoun(value) ? PronounLabels[value] : value;
